import React, { useState, useCallback } from 'react';
import { SmartPDFProcessorService, type SmartPDFProcessingResult } from '../../shared/services/smart-pdf-processor.service';
import { useIngresos } from '../../modules/ingresos/hooks/useIngresos';
import { useEgresos } from '../../modules/egresos/hooks/useEgresos';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import { LoggerService } from '../../shared/services/logger.service';

export const SmartPDFUploader: React.FC = () => {
  const { user } = useAuth();
  const { createIngreso } = useIngresos();
  const { createEgreso } = useEgresos();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [results, setResults] = useState<SmartPDFProcessingResult[]>([]);
  const [currentStep, setCurrentStep] = useState<string>('');

  const pdfProcessor = new SmartPDFProcessorService();

  const processFiles = useCallback(async (files: File[]) => {
    if (!user) {
      alert('Debes estar autenticado para procesar PDFs');
      return;
    }

    if (!pdfProcessor.isConfigured()) {
      alert('OpenAI API no está configurada. Por favor configura tu API key.');
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setResults([]);
    setCurrentStep('Iniciando procesamiento...');

    LoggerService.info('Starting PDF batch processing', 'SmartPDFUploader', {
      fileCount: files.length,
      userId: user.uid
    });

    try {
      const processedResults: SmartPDFProcessingResult[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentStep(`Procesando ${file.name}...`);
        setProcessingProgress((i / files.length) * 50); // 50% para procesamiento

        const result = await pdfProcessor.processPDF(file);
        processedResults.push(result);

        if (result.success && result.data) {
          setCurrentStep(`Guardando transacciones de ${file.name}...`);
          await saveTransactions(result.data);
        }

        setProcessingProgress(((i + 1) / files.length) * 100);
      }

      setResults(processedResults);
      setCurrentStep('¡Procesamiento completado!');

      LoggerService.logUserAction('pdf_batch_processing_completed', 'SmartPDFUploader', {
        fileCount: files.length,
        successCount: processedResults.filter(r => r.success).length,
        userId: user.uid
      });

    } catch (error) {
      LoggerService.error('PDF batch processing failed', 'SmartPDFUploader', { error });
      setCurrentStep('Error en el procesamiento');
    } finally {
      setIsProcessing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      alert('Por favor selecciona solo archivos PDF');
      return;
    }

    processFiles(pdfFiles);
  }, [processFiles]);

  const saveTransactions = async (data: NonNullable<SmartPDFProcessingResult['data']>) => {
    if (!user?.uid) {
      LoggerService.error('Cannot save transactions: user not authenticated', 'SmartPDFUploader');
      return;
    }

    // Guardar ingresos
    for (const ingreso of data.ingresos) {
      try {
        const ingresoData = {
          concepto: ingreso.concepto,
          monto: ingreso.monto,
          categoria: ingreso.categoria,
          fecha: typeof ingreso.fecha === 'string' ? ingreso.fecha : ingreso.fecha.toISOString().split('T')[0],
          descripcion: ingreso.descripcion || '',
          userId: user.uid
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ingresoId = await (createIngreso as any)(ingresoData);
        LoggerService.info('Ingreso created from PDF', 'SmartPDFUploader', { ingresoId });
      } catch (error) {
        LoggerService.error('Failed to create ingreso from PDF', 'SmartPDFUploader', { error, ingreso });
      }
    }

    // Guardar egresos
    for (const egreso of data.egresos) {
      try {
        const egresoData = {
          concepto: egreso.concepto,
          monto: egreso.monto,
          categoria: egreso.categoria,
          fecha: typeof egreso.fecha === 'string' ? egreso.fecha : egreso.fecha.toISOString().split('T')[0],
          descripcion: egreso.descripcion || '',
          userId: user.uid
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const egresoId = await (createEgreso as any)(egresoData);
        LoggerService.info('Egreso created from PDF', 'SmartPDFUploader', { egresoId });
      } catch (error) {
        LoggerService.error('Failed to create egreso from PDF', 'SmartPDFUploader', { error, egreso });
      }
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const getTotalStats = () => {
    const successResults = results.filter(r => r.success && r.data);
    const totalIngresos = successResults.reduce((sum, r) => sum + (r.data?.ingresos.length || 0), 0);
    const totalEgresos = successResults.reduce((sum, r) => sum + (r.data?.egresos.length || 0), 0);
    const totalTransacciones = totalIngresos + totalEgresos;

    return { totalIngresos, totalEgresos, totalTransacciones };
  };

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center text-gray-600">
          <p className="mb-2">🔐 Autenticación requerida</p>
          <p className="text-sm">Inicia sesión para procesar documentos PDF con IA</p>
        </div>
      </div>
    );
  }

  if (!pdfProcessor.isConfigured()) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center text-amber-600">
          <p className="mb-2">⚠️ OpenAI API no configurada</p>
          <p className="text-sm">Configura tu API key de OpenAI para procesar PDFs con inteligencia artificial</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">
        🤖 Procesador Inteligente de PDFs
      </h3>

      <div className="space-y-4">
        {/* Información del servicio */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-blue-800 text-sm mb-2">
            <strong>🧠 ¿Cómo funciona?</strong>
          </p>
          <ul className="text-blue-700 text-sm space-y-1">
            <li>• Sube tus extractos bancarios o documentos financieros en PDF</li>
            <li>• La IA extrae automáticamente ingresos y egresos</li>
            <li>• Las transacciones se categorizan y guardan en tu cuenta</li>
            <li>• ¡Todo automático, sin tipear manualmente!</li>
          </ul>
        </div>

        {/* Área de carga */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragOver
              ? 'border-blue-500 bg-blue-50'
              : isProcessing
                ? 'border-gray-300 bg-gray-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isProcessing ? (
            <div className="space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-blue-800 font-medium">{currentStep}</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${processingProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">{processingProgress.toFixed(0)}% completado</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-4xl">📄</div>
              <p className="text-lg font-medium text-gray-700">
                Arrastra tus PDFs aquí o haz clic para seleccionar
              </p>
              <p className="text-sm text-gray-500">
                Formatos soportados: PDF • Tamaño máximo: 10MB
              </p>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
              >
                Seleccionar PDFs
              </label>
            </div>
          )}
        </div>

        {/* Resultados */}
        {results.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700">📊 Resultados del Procesamiento</h4>
            
            {/* Estadísticas generales */}
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-green-600">{getTotalStats().totalIngresos}</p>
                  <p className="text-sm text-green-700">Ingresos</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{getTotalStats().totalEgresos}</p>
                  <p className="text-sm text-red-700">Egresos</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{getTotalStats().totalTransacciones}</p>
                  <p className="text-sm text-blue-700">Total</p>
                </div>
              </div>
            </div>

            {/* Detalles por archivo */}
            <div className="space-y-2">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded border ${
                    result.success 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={result.success ? 'text-green-600' : 'text-red-600'}>
                        {result.success ? '✅' : '❌'}
                      </span>
                      <span className="font-medium">{result.metadata?.fileName}</span>
                    </div>
                    {result.success && result.data && (
                      <div className="text-sm text-gray-600">
                        {result.data.ingresos.length + result.data.egresos.length} transacciones
                      </div>
                    )}
                  </div>
                  {result.error && (
                    <p className="text-sm text-red-600 mt-1">{result.error}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};